import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTablePFSEvent } from "primereact/datatable";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import {
  useGetFinixWebhooksQuery,
  useGetFinixWebhookStatsQuery,
  useRetryFinixWebhookMutation,
} from "store/queries/finixOps";
import {
  FinixWebhookStatus,
  IFinixWebhookItem,
  IFinixWebhookStatusCounts,
} from "types/FinixOps";

import { apiErrorMessage, formatDateTime } from "../../utils/hstImports";

const PAGE_SIZE = 25;

// The table lists only what a person can act on: FAILED webhooks from the last 90 days, newest first.
export const FAILED_WEBHOOKS_WINDOW_DAYS = 90;

const STATUS_LABEL: Record<FinixWebhookStatus, string> = {
  PENDING: "Pending",
  PROCESSED: "Processed",
  FAILED: "Failed",
  SKIPPED: "Skipped",
};

const STATUS_SEVERITY: Record<
  FinixWebhookStatus,
  "success" | "danger" | "warning" | "info"
> = {
  PENDING: "info",
  PROCESSED: "success",
  FAILED: "danger",
  SKIPPED: "warning",
};

const RANGE_OPTIONS = [
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
];

// Received / processed / failed / skipped, as the ticket lists them.
const CARD_KEYS: (keyof IFinixWebhookStatusCounts)[] = [
  "received",
  "processed",
  "failed",
  "skipped",
];

const CARD_LABEL: Record<keyof IFinixWebhookStatusCounts, string> = {
  received: "Received",
  processed: "Processed",
  failed: "Failed",
  skipped: "Skipped",
  pending: "Pending",
};

/** The bar chart's data: one bar pair per UTC day, received vs processed. */
export const buildWebhookChartData = (
  points: { date: string; received: number; processed: number }[]
) => ({
  labels: points.map((p) => p.date.slice(5)),
  datasets: [
    {
      type: "bar",
      label: "Received",
      backgroundColor: "#7C71FD",
      data: points.map((p) => p.received),
    },
    {
      type: "bar",
      label: "Processed",
      backgroundColor: "#0ECFA9",
      data: points.map((p) => p.processed),
    },
  ],
});

const CHART_OPTIONS = {
  maintainAspectRatio: false,
  maxBarThickness: 20,
  plugins: {
    legend: { align: "start" },
    tooltip: { mode: "index", intersect: false },
  },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
};

const statusBody = (w: IFinixWebhookItem) => (
  <Tag value={STATUS_LABEL[w.status]} severity={STATUS_SEVERITY[w.status]} />
);

const receivedBody = (w: IFinixWebhookItem) => formatDateTime(w.createdAt);

const processedBody = (w: IFinixWebhookItem) => formatDateTime(w.processedAt);

const errorBody = (w: IFinixWebhookItem) =>
  w.lastError ? (
    <span
      className={w.status === "FAILED" ? "text-red-600" : "text-600"}
      style={{ wordBreak: "break-word" }}
    >
      {w.lastError}
    </span>
  ) : null;

// One panel per time window, so the window's name sits as a heading over its four figures rather than as
// a small caption floating between two identical rows of numbers.
function CountCards({
  title,
  counts,
  loading,
  testIdPrefix,
}: {
  title: string;
  counts: IFinixWebhookStatusCounts | undefined;
  loading: boolean;
  testIdPrefix: string;
}) {
  return (
    <section
      className="surface-card border-1 surface-border border-round p-3 h-full"
      aria-labelledby={`${testIdPrefix}-title`}
    >
      <h3
        id={`${testIdPrefix}-title`}
        className="mt-0 mb-3 text-base font-semibold text-900 flex align-items-center gap-2"
      >
        <i className="pi pi-calendar text-600" aria-hidden="true" />
        {title}
      </h3>
      <div className="grid">
        {CARD_KEYS.map((key) => (
          <div key={key} className="col-6 md:col-3">
            <div
              className="surface-ground border-round p-3 text-center"
              data-testid={`${testIdPrefix}-${key}`}
            >
              <div className="text-2xl font-bold">
                {loading && !counts ? "…" : counts?.[key] ?? 0}
              </div>
              <div className="text-600 text-sm">{CARD_LABEL[key]}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function FinixWebhooksSection() {
  const toastRef = useContext(ToastContext);
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const { data: statsData, isFetching: statsLoading } =
    useGetFinixWebhookStatsQuery(days);
  const { data, isFetching } = useGetFinixWebhooksQuery({
    status: "FAILED",
    sinceDays: FAILED_WEBHOOKS_WINDOW_DAYS,
    page,
    pageSize: PAGE_SIZE,
  });
  const [retryWebhook] = useRetryFinixWebhookMutation();

  const stats = statsData?.data;
  const chartData = useMemo(
    () => buildWebhookChartData(stats?.series.points || []),
    [stats]
  );
  const list = data?.data;

  const onPage = (event: DataTablePFSEvent) => {
    setPage(Math.floor(event.first / event.rows) + 1);
  };

  const onRetry = async (webhook: IFinixWebhookItem) => {
    setRetryingId(webhook.id);
    try {
      await retryWebhook(webhook.id).unwrap();
      toastRef?.current?.show({
        severity: "success",
        summary: "Retry queued",
        detail:
          "The webhook is pending again; the webhook job picks it up within a couple of minutes.",
      });
    } catch (error) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Could not retry",
        detail: apiErrorMessage(error).message,
      });
    } finally {
      setRetryingId(null);
    }
  };

  const actionsBody = (w: IFinixWebhookItem) =>
    w.status === "FAILED" ? (
      <Button
        label="Retry"
        icon="pi pi-refresh"
        className="p-button-text p-button-sm"
        loading={retryingId === w.id}
        disabled={retryingId !== null}
        onClick={() => onRetry(w)}
        data-testid={`finix-webhook-retry-${w.id}`}
      />
    ) : null;

  return (
    <div className="flex flex-column gap-3">
      <div className="grid">
        <div className="col-12 lg:col-6">
          <CountCards
            title="Last 7 days"
            counts={stats?.last7Days}
            loading={statsLoading}
            testIdPrefix="finix-webhooks-7d"
          />
        </div>
        <div className="col-12 lg:col-6">
          <CountCards
            title="Last 30 days"
            counts={stats?.last30Days}
            loading={statsLoading}
            testIdPrefix="finix-webhooks-30d"
          />
        </div>
      </div>

      <div className="flex flex-column gap-2">
        <div className="flex flex-wrap align-items-center justify-content-between gap-2">
          <span className="font-semibold">Received and processed per day</span>
          <SelectButton
            value={days}
            options={RANGE_OPTIONS}
            onChange={(e) => e.value && setDays(e.value)}
            aria-label="Chart range"
          />
        </div>
        <div style={{ height: "260px" }}>
          <Chart
            type="bar"
            data={chartData}
            options={CHART_OPTIONS}
            style={{ height: "100%" }}
          />
        </div>
        <small className="text-600">
          UTC days. Received counts every webhook by when it arrived; processed
          counts the ones marked processed, by when.
        </small>
      </div>

      <div className="flex flex-wrap align-items-baseline justify-content-between gap-3">
        <span className="font-semibold">Failed webhooks</span>
        <small className="text-600">
          Last {FAILED_WEBHOOKS_WINDOW_DAYS} days, newest first. Retry puts a
          webhook back in the queue; the job picks it up within a minute.
        </small>
      </div>

      <PageDataTable
        value={list?.items || []}
        dataKey="id"
        lazy
        paginator
        first={(page - 1) * PAGE_SIZE}
        rows={PAGE_SIZE}
        totalRecords={list?.total || 0}
        onPage={onPage}
        loading={isFetching}
        emptyMessage={`No failed Finix webhooks in the last ${FAILED_WEBHOOKS_WINDOW_DAYS} days.`}
      >
        <Column header="Received" body={receivedBody} />
        <Column header="Entity" field="entityType" />
        <Column header="Event" field="eventType" />
        <Column header="Entity id" field="entityId" />
        <Column header="Status" body={statusBody} />
        <Column header="Attempts" field="attempts" />
        <Column header="Processed" body={processedBody} />
        <Column header="Error" body={errorBody} />
        <Column header="" body={actionsBody} style={{ width: "7rem" }} />
      </PageDataTable>
    </div>
  );
}
